import { createFeatureSelector, createSelector } from "@ngrx/store";
import { BankAccountOptions } from "../../interfaces/bank-account-form";

/**
 * Creates a feature selector for the 'bankAccountForm' feature.
 * 
 * This selector retrieves the entire state of the `bankAccountForm` feature from the store.
 * 
 * @returns A memoized selector that returns the `bankAccountForm` feature state.
 */
export const bankAccountFormFeature = createFeatureSelector<BankAccountOptions>('bankAccountForm');

/**
 * Retrieves the entire `BankAccountOptions` state.
 * 
 * @param data - The current state of the `bankAccountForm` feature.
 * @returns The `BankAccountOptions` object containing all form-related data.
 */
export const getBankAccountData = (data: BankAccountOptions): BankAccountOptions => data;

/**
 * Retrieves the error message from the `BankAccountErrorData` object.
 * 
 * @param data - The current state of the `bankAccountForm` feature.
 * @returns A string containing the error message, or an empty string if no error exists.
 */
const getBankAccountErrorDataMessage = (data: BankAccountOptions): string => data.bankAccountErrorData?.message;

/**
 * Selector to retrieve the entire `BankAccountOptions` state.
 * 
 * This selector is useful for accessing all form-related data in a single object.
 * 
 * @returns A memoized selector that returns the `BankAccountOptions` state.
 */
export const getBankAccountFormDataSelector = createSelector(
    bankAccountFormFeature,
    getBankAccountData
);

/**
 * Selector to retrieve the error message from the `BankAccountErrorData` object.
 * 
 * This selector is useful for displaying error messages related to bank account operations.
 * 
 * @returns A memoized selector that returns the error message as a string.
 */
export const getBankAccountErrorDataMessageSelector = createSelector(
    bankAccountFormFeature,
    getBankAccountErrorDataMessage
);